import authApiClient, { getCurrentCSRFToken } from '../authApiClient';
import { buildApiUrl } from './apiBase';
import { isFeatureEnabled } from './featureFlags';
import { readLastTurnIdForSession } from './journeyTurnLinkStore';
import { getOrCreateTestRunId, isJourneyE2ETestModeEnabled } from './testRunContext';

const OUTCOME_LOOP_FLAG = 'ff_outcome_loop_v1';
const OUTCOME_EVENT_TYPES = new Set([
  'served',
  'started',
  'completed',
  'reflected',
  'abandoned',
  'implicit_completed',
  'nav_failed',
  'undo',
  'switch',
  'pause',
  'resume'
]);

const PAGE_MAP = Object.freeze({
  '/app/journey-guide': 'journey_guide',
  '/app/journal': 'journal',
  '/app/goals': 'goals',
  '/app/evaluation': 'evaluation',
  '/app/inspiration': 'inspiration',
  '/app/quickadvice': 'quickadvice',
  '/app/challenges': 'challenges',
  '/app/gift': 'gift',
  '/app/quiz': 'quiz',
  '/app/recommendations': 'recommendations',
  '/app/earth': 'elements',
  '/app/water': 'elements',
  '/app/air': 'elements',
  '/app/fire': 'elements',
  '/app/practitioners/patients': 'practitioner_patient',
  '/app/practitioner-space': 'practitioners',
  '/app/practitioner-dashboard': 'practitioners',
  '/app/practitioners': 'practitioners',
  '/app/insights': 'insights_reminders',
  '/app/reminders': 'insights_reminders',
  '/app/insightsandreminders': 'insights_reminders',
  '/app/passion-chat': 'passion_chat',
  '/app/passion-chat-single': 'passion_chat',
  '/app/mini-me-chat': 'mini_me_chat',
  '/app/mini-me': 'mini_me_chat'
});

function matchesRoute(path = '', route = '') {
  return path === route || path.startsWith(`${route}/`);
}

export function getPageKeyFromPath(pathname = '') {
  const path = String(pathname || '').trim().toLowerCase();
  if (!path) return 'other';
  const routeMatch = Object.entries(PAGE_MAP).find(([route]) => matchesRoute(path, route));
  return routeMatch?.[1] || 'other';
}

export function buildOutcomeTurnId() {
  try {
    if (typeof window !== 'undefined' && window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }
  } catch (error) {
    // fallback below
  }
  return `turn_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function normalizeOutcomePayload(payload = {}) {
  const missionSessionId = String(payload.missionSessionId || payload.activeMissionId || '').trim();
  const turnId = String(payload.turnId || buildOutcomeTurnId()).trim();
  const lastTurnId = String(
    payload.lastTurnId
    || readLastTurnIdForSession(missionSessionId)
    || turnId
  ).trim();
  const resolvedTestRunId = String(
    payload.testRunId
    || (isJourneyE2ETestModeEnabled() ? getOrCreateTestRunId() : '')
    || ''
  ).trim() || null;
  const normalizedMeta = payload.meta && typeof payload.meta === 'object'
    ? { ...payload.meta }
    : {};
  if (resolvedTestRunId && !normalizedMeta.testRunId) {
    normalizedMeta.testRunId = resolvedTestRunId;
  }

  const rawEventType = String(payload.eventType || '').trim().toLowerCase();
  const normalizedEventType = rawEventType === 'smart_question_request'
    ? 'resume'
    : rawEventType;
  return {
    missionSessionId,
    testRunId: resolvedTestRunId,
    turnId,
    lastTurnId,
    pageKey: String(payload.pageKey || 'other').trim().toLowerCase() || 'other',
    eventType: OUTCOME_EVENT_TYPES.has(normalizedEventType) ? normalizedEventType : null,
    route: payload.route || null,
    chipId: payload.chipId || null,
    chipLabel: payload.chipLabel || null,
    consentState: payload.consentState || 'unknown',
    confidence: payload.confidence ?? null,
    risk: payload.risk ?? null,
    clientTs: payload.clientTs || new Date().toISOString(),
    meta: normalizedMeta
  };
}

function hasRequiredOutcomeFields(payload = {}) {
  return Boolean(
    payload.missionSessionId
    && payload.turnId
    && payload.lastTurnId
    && payload.pageKey
    && payload.eventType
  );
}

export function buildOutcomeKeepaliveHeaders({
  accessToken = '',
  csrfToken = '',
  testRunId = ''
} = {}) {
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${accessToken || ''}`,
    ...(csrfToken ? { 'X-FS-XSRF': csrfToken } : {}),
    ...(testRunId ? { 'x-test-run-id': testRunId } : {})
  };
}

export async function emitOutcomeEvent(payload = {}, options = {}) {
  if (!isFeatureEnabled(OUTCOME_LOOP_FLAG, true)) {
    return { ok: true, skipped: true, reason: 'feature_flag_disabled' };
  }

  const normalized = normalizeOutcomePayload(payload);
  if (!hasRequiredOutcomeFields(normalized)) {
    return { ok: false, skipped: true, reason: 'missing_required_fields', normalized };
  }

  const keepalive = options.keepalive === true;
  if (keepalive) {
    try {
      const token = localStorage.getItem('token') || '';
      const csrfToken = getCurrentCSRFToken();
      await fetch(buildApiUrl('/journey-guide/outcome-event'), {
        method: 'POST',
        credentials: 'include',
        headers: buildOutcomeKeepaliveHeaders({
          accessToken: token,
          csrfToken,
          testRunId: normalized.testRunId
        }),
        body: JSON.stringify(normalized),
        keepalive: true
      });
      return { ok: true };
    } catch (error) {
      return { ok: false, error: error.message };
    }
  }

  try {
    const response = await authApiClient.post('/journey-guide/outcome-event', normalized);
    return { ok: true, data: response?.data || null };
  } catch (error) {
    return { ok: false, error: error?.message || 'outcome_event_failed' };
  }
}

export { OUTCOME_LOOP_FLAG };
